"use client";

import React, { useState } from "react";
import PropTypes from "prop-types";

export function PostcodeSearch({
  buttonLabel,
  inputPlaceholder = "Enter your postcode",
  id,
  align = "center",
  autoFocus = false,
}) {
  const [postcode, setPostcode] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [responseMessageType, setResponseMessageType] = useState("error");

  let alignClasses = "";
  switch (align) {
    case "left":
      alignClasses = "text-left";
      break;
    case "right":
      alignClasses = "text-right";
      break;
    default:
      alignClasses = "mx-auto";
      break;
  }

  const handlePostcodeChange = (e) => {
    setPostcode(e.target.value);
    setResponseMessage(""); // Clear response message when user starts typing
  };

  const handleButtonClick = async () => {
    const trimmedPostcode = postcode.trim();
    const encodedPostcode = encodeURIComponent(trimmedPostcode);

    if (!trimmedPostcode) {
      setResponseMessage("Please enter your postcode.");
      setResponseMessageType("error");
      return;
    }

    try {
      const response = await fetch(
        `https://api.getsetclean.co.uk/postcodes/check/${encodedPostcode}`,
        {
          method: "GET",
        }
      );

      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }

      const message = data.message || data.error || "";

      if (!response.ok || data.err) {
        setResponseMessage(
          message ||
            (response.status === 404
              ? "We do not currently cover your area."
              : "Unable to check this postcode."),
        );
        setResponseMessageType("error");
      } else if (message === "serviceAvailable") {
        const redirectUrl = new URL("https://app.getsetclean.co.uk/booking/0");
        redirectUrl.searchParams.set("postcode", trimmedPostcode);
        window.location.href = redirectUrl.toString();
      } else if (data.redirect) {
        const redirectUrl = new URL(data.redirect);
        redirectUrl.searchParams.append("code", trimmedPostcode);
        window.location.href = redirectUrl.toString(); // Redirect to the specified URL with the appended query parameter
      } else {
        setResponseMessage(message); // Display the received API message
        setResponseMessageType("success");
      }
    } catch (error) {
      setResponseMessage("An unexpected error occurred. Please try again later.");
      setResponseMessageType("error");
    }
  };

  return (
    <div className="w-full">
      <div
        className={`flex items-center gap-2 ${alignClasses} p-1 h-12 bg-white border border-gray-200 rounded-full w-full `}
      >
        <input
          id={id}
          name="postcode"
          type="text"
          placeholder={inputPlaceholder}
          value={postcode}
          onChange={handlePostcodeChange}
          className="flex-grow text-base px-4 text-gray-600 focus:outline-none"
          autoFocus={autoFocus}
        />
        <button
          onClick={handleButtonClick}
          className="bg-primary hover:bg-black text-white text-base font-medium h-full px-4 rounded-full transition duration-300 ease-in-out focus:outline-none focus:ring-4 "
        >
          {buttonLabel || "Book now"}
        </button>
      </div>
      {responseMessage && (
        <div
          className={`mt-2 text-sm ${
            responseMessageType === "error" ? "text-red-600" : "text-green-700"
          }`}
        >
          {responseMessage}
        </div>
      )}
    </div>
  );
}

PostcodeSearch.propTypes = {
  buttonLabel: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  id: PropTypes.string,
  align: PropTypes.oneOf(["left", "center", "right"]),
};
